/* eslint-disable react-refresh/only-export-components -- the scene library is bundled for the renderer, not hot-reloaded; its helpers live beside the components on purpose */
/**
 * The whole video: each scene in its own Sequence, overlapping the previous one by its transition (fade, slide or
 * wipe — the incoming scene animates over the outgoing one; a cut has no overlap), plus music and voice-over under
 * everything. Used as-is by the in-app Player and by the MP4 renderer (Root.tsx).
 *
 * Transitions are ours rather than @remotion/transitions: its 4.0.532 ESM build inlines React 19 internals and
 * breaks the render bundle on the app's React 18.
 */
import type { CSSProperties, ReactNode } from 'react';
import { AbsoluteFill, Audio, Easing, Sequence, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import { useMemo } from 'react';
import { CaptionLayer, SceneView, TextLayerView } from './scenes';
import { captionCues } from './captions';
import { BrandGround, CaptionLayerContext, Logo, Packshot, MotifContext, ProxyContext, RegionContext, SourcesContext, Rise, TextScaleContext, aspectOf, bodyStack, palette, useBrandFonts, useEnter, useUnit } from './kit';
import { overlapFrames, sceneStarts, secondsToFrames, stageSpan, totalFrames, voicePartsOf, type BrandStage, type SceneTransition, type VideoSpec } from './spec';

/** proxies: play each clip's light copy (looking, never exporting). */
export interface VideoCompositionProps { spec: VideoSpec; proxies?: boolean; /** Clips already downloaded (URL → blob URL), the editor's preview only. */ sources?: Record<string, string>; [key: string]: unknown }

/** The incoming scene's entrance over its first `frames` frames. */
function Enter({ kind, frames, children }: { kind: SceneTransition; frames: number; children: ReactNode }) {
  const frame = useCurrentFrame();
  if (frames <= 0 || kind === 'cut') return <>{children}</>;
  const p = interpolate(frame, [0, frames], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.inOut(Easing.cubic) });
  if (kind === 'slide') return <AbsoluteFill style={{ transform: `translateX(${(1 - p) * 100}%)` }}>{children}</AbsoluteFill>;
  if (kind === 'wipe') return <AbsoluteFill style={{ clipPath: `inset(0 ${(1 - p) * 100}% 0 0)` }}>{children}</AbsoluteFill>;
  return <AbsoluteFill style={{ opacity: p }}>{children}</AbsoluteFill>;
}

/** The 'bug' motif: the logo small in a top corner, on every scene before the end card — top left on 9:16 (above the
 * text's safe area), top right elsewhere (text is left-aligned, so the right corner is free). */
function LogoBug({ spec }: { spec: VideoSpec }) {
  const u = useUnit();
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const side = aspectOf(width, height) === '9:16' ? 'flex-start' : 'flex-end';
  const opacity = interpolate(frame, [0, 10], [0, 0.92], { extrapolateRight: 'clamp' });
  return (
    <AbsoluteFill style={{ padding: 56 * u, alignItems: side, justifyContent: 'flex-start', pointerEvents: 'none' }}>
      {/* A soft dark backing so the light logo reads on every ground (the brand colour can be light). */}
      <div style={{ opacity, color: '#FFFFFF', background: 'rgba(0,0,0,0.32)', padding: `${12 * u}px ${18 * u}px`, borderRadius: 14 * u, display: 'flex' }}><Logo theme={spec.theme} onDark height={44 * u} /></div>
    </AbsoluteFill>
  );
}

/** The share of the frame's width the brand stage takes (landscape). */
const STAGE_SHARE = 0.5;

/**
 * The brand stage's panel: logo, the claim, the products together and the legal line — it stays while the scenes play
 * in the rest of the frame (the brand ground runs under both). Text sits in the title-safe 90 %.
 */
function StagePanel({ stage, spec }: { stage: BrandStage; spec: VideoSpec }) {
  const u = useUnit();
  const { width, height } = useVideoConfig();
  const pal = palette(spec.theme, 'brand');
  const logo = useEnter(0, 16);
  const products = (stage.products || []).filter((m) => m.kind === 'image').slice(0, 4);
  const total = Math.max(1, useVideoConfig().durationInFrames);
  const pad = { x: width * 0.05, y: height * 0.05 };
  return (
    <AbsoluteFill style={{ left: stage.side === 'right' ? width * (1 - STAGE_SHARE) : 0, width: width * STAGE_SHARE, padding: `${pad.y}px ${pad.x * 0.8}px ${pad.y}px ${pad.x}px`, alignItems: 'center', justifyContent: 'center', gap: 34 * u, textAlign: 'center' }}>
      <div style={{ opacity: logo, transform: `scale(${0.9 + 0.1 * logo})`, color: pal.text }}>
        {/* Capped at the panel's width (in pixels), not the frame's: a wide wordmark stays inside the panel. */}
        <Logo theme={spec.theme} onDark={pal.dark} height={(products.length ? 120 : 160) * u} style={{ maxWidth: Math.round(width * STAGE_SHARE * 0.78) }} />
      </div>
      {stage.claim && <Rise delay={6} style={{ fontFamily: bodyStack(spec.theme), fontWeight: 600, fontSize: 52 * u, lineHeight: 1.15, color: pal.text, maxWidth: '92%', textWrap: 'balance' as CSSProperties['textWrap'] }}>{stage.claim}</Rise>}
      {products.length > 0 && (
        // Packshots in their true colours: on a light plate on a coloured ground (Packshot), side by side.
        <Rise delay={12} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 18 * u, height: 420 * u, width: '86%' }}>
          {products.map((m, i) => <Packshot key={`${m.url}-${i}`} media={m} ground={pal.ground} total={total} height="100%" style={{ flex: 1 }} />)}
        </Rise>
      )}
      {stage.legal && <div style={{ position: 'absolute', left: pad.x, right: pad.x * 0.8, bottom: pad.y * 0.6, fontFamily: bodyStack(spec.theme), fontSize: 17 * u, lineHeight: 1.35, color: pal.muted }}>{stage.legal}</div>}
    </AbsoluteFill>
  );
}

/** A scene (or layer) drawn in the part of the frame the brand stage leaves free; null = the whole frame. */
function InRegion({ stage, children }: { stage: BrandStage | null; children: ReactNode }) {
  const { width, height } = useVideoConfig();
  if (!stage) return <>{children}</>;
  const w = Math.round(width * (1 - STAGE_SHARE));
  return (
    // AbsoluteFill is 100 % wide by default: the box needs its own width, not only an offset.
    <AbsoluteFill style={{ left: stage.side === 'right' ? 0 : width - w, width: w }}>
      <RegionContext.Provider value={{ width: w, height, framed: true }}>{children}</RegionContext.Provider>
    </AbsoluteFill>
  );
}

/** The 'frame' motif: a thin accent border round the whole video. */
function AccentFrame({ spec }: { spec: VideoSpec }) {
  const u = useUnit();
  return <AbsoluteFill style={{ border: `${14 * u}px solid ${palette(spec.theme, 'dark').accent}`, pointerEvents: 'none' }} />;
}

/** The bed under speech: down to ~18% (≈ −15 dB) while someone speaks, ramping down 0.25 s before the first word
 * and back up over 0.35 s after the last — speech stays ≥ 10 LU above the music. */
const DUCK = 0.18;
function duckAt(f: number, spans: Array<{ start: number; end: number }>, fps: number): number {
  let g = 1;
  for (const s of spans) {
    const a = s.start * fps; const b = s.end * fps;
    const pre = 0.25 * fps; const post = 0.35 * fps;
    if (f < a - pre || f > b + post) continue;
    const v = f < a ? interpolate(f, [a - pre, a], [1, DUCK]) : f > b ? interpolate(f, [b, b + post], [DUCK, 1]) : DUCK;
    g = Math.min(g, v);
  }
  return g;
}
/** The bed starts at once (the hook needs its energy) and fades over the last ~0.7 s. */
const edges = (f: number, total: number) => interpolate(f, [0, 2, Math.max(3, total - 20), Math.max(4, total)], [0, 1, 1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

export function VideoComposition({ spec, proxies, sources }: VideoCompositionProps) {
  useBrandFonts(spec.theme);
  const { durationInFrames } = useVideoConfig();
  const starts = sceneStarts(spec);
  const music = spec.audio?.musicUrl;
  const voice = voicePartsOf(spec.audio).length > 0;
  const musicVolume = spec.audio?.musicVolume ?? (voice ? 0.35 : 0.55);
  const { fps } = useVideoConfig();
  const ducks = spec.audio?.duck || [];
  const motif = spec.art?.motif || 'none';
  const endAt = spec.scenes.findIndex((s) => s.kind === 'endcard');
  const bugUntil = endAt > 0 ? starts[endAt] : durationInFrames;
  // The bug stays off product scenes: the packshot carries the brand, and a corner chip would sit on its plate.
  const bugRuns = useMemo(() => {
    const runs: { from: number; to: number }[] = [];
    spec.scenes.forEach((sc, i) => {
      const from = starts[i], to = Math.min(bugUntil, starts[i + 1] ?? durationInFrames);
      if (sc.kind === 'product' || from >= to) return;
      const last = runs[runs.length - 1];
      if (last && last.to === from) last.to = to; else runs.push({ from, to });
    });
    return runs;
  }, [spec.scenes, starts, bugUntil, durationInFrames]);
  const layer = !!spec.transcript;
  const cues = useMemo(() => (spec.transcript?.on ? captionCues(spec) : []), [spec]);
  // The brand stage: on 16:9 only (a panel beside the scenes needs the width), for its span.
  const frameSize = useVideoConfig();
  const stage = spec.stage && aspectOf(frameSize.width, frameSize.height) === '16:9' ? spec.stage : null;
  const span = stage ? stageSpan(spec) : null;
  const spanFrames = span ? [Math.round(span[0] * fps), Math.round(span[1] * fps)] as const : null;
  const onStage = (frame: number) => !!spanFrames && frame >= spanFrames[0] && frame < spanFrames[1];
  return (
    <ProxyContext.Provider value={!!proxies}>
    <SourcesContext.Provider value={sources || null}>
    <MotifContext.Provider value={motif}>
    <CaptionLayerContext.Provider value={layer}>
      {/* text-wrap is inherited: every line in every scene breaks into balanced lines (no word left alone on the last one). */}
      {/* A word wider than its box breaks inside it rather than running off the frame (long compounds in sv/nb/de). */}
      <AbsoluteFill style={{ background: '#000000', textWrap: 'balance' as CSSProperties['textWrap'], overflowWrap: 'break-word' }}>
      {stage && spanFrames && <Sequence from={spanFrames[0]} durationInFrames={Math.max(1, spanFrames[1] - spanFrames[0])} name="brand ground"><BrandGround theme={spec.theme} /></Sequence>}
      {spec.scenes.map((scene, i) => (
        // Mounted 3 s early: the next clip is loaded and seeked when the cut comes. Creator files are big, so 1.5 s was
        // not enough and cuts showed black; a clip still not ready pauses the preview (pauseWhenBuffering in Media).
        <Sequence key={scene.id} from={starts[i]} durationInFrames={secondsToFrames(scene.duration)} premountFor={Math.round(fps * 3)} name={`${i + 1}. ${scene.kind}`}>
          <Enter kind={scene.transition || 'fade'} frames={overlapFrames(spec.scenes, i)}>
            <TextScaleContext.Provider value={scene.textScale || 1}>
              <InRegion stage={onStage(starts[i]) ? stage : null}>
                <SceneView scene={scene} theme={spec.theme} captionStyle={spec.captionStyle || 'bold'} />
              </InRegion>
            </TextScaleContext.Provider>
          </Enter>
        </Sequence>
      ))}
      {(spec.texts || []).map((t) => (
        <Sequence key={`text-${t.id}`} from={Math.max(0, Math.round(t.at * fps))} durationInFrames={Math.max(1, Math.round(t.seconds * fps))} name={`text: ${t.text.slice(0, 24)}`}>
          <TextLayerView layer={t} theme={spec.theme} />
        </Sequence>
      ))}
      {music && <Audio src={music} trimBefore={Math.round((spec.audio?.musicStart || 0) * fps)} volume={(f) => musicVolume * duckAt(f, ducks, fps) * edges(f, durationInFrames)} />}
      {voicePartsOf(spec.audio).map((v, i) => (
        <Sequence key={`vo-${v.id}-${i}`} from={Math.round(v.at * fps)} durationInFrames={Math.max(1, Math.round(v.seconds * fps))} premountFor={Math.round(fps)} name={`voice-over ${i + 1}`}>
          <Audio src={v.url} trimBefore={Math.round((v.from || 0) * fps)} volume={spec.audio?.voiceoverVolume ?? 1} />
        </Sequence>
      ))}
      {(spec.audio?.sfx || []).map((x, i) => (
        <Sequence key={`sfx-${i}`} from={Math.max(0, Math.round(x.at * fps))} durationInFrames={Math.round(2 * fps)} name={`sfx ${i + 1}`}>
          <Audio src={x.url} volume={x.volume ?? 0.6} />
        </Sequence>
      ))}
      {stage && spanFrames && <Sequence from={spanFrames[0]} durationInFrames={Math.max(1, spanFrames[1] - spanFrames[0])} name="brand stage"><StagePanel stage={stage} spec={spec} /></Sequence>}
      {cues.length > 0 && <InRegion stage={stage}><CaptionLayer spec={spec} cues={cues} /></InRegion>}
      {motif === 'bug' && !stage && bugRuns.map((r) => <Sequence key={r.from} from={r.from} durationInFrames={r.to - r.from} name="logo bug"><LogoBug spec={spec} /></Sequence>)}
      {motif === 'frame' && <AccentFrame spec={spec} />}
    </AbsoluteFill>
    </CaptionLayerContext.Provider>
    </MotifContext.Provider>
    </SourcesContext.Provider>
    </ProxyContext.Provider>
  );
}

export const videoDuration = (spec: VideoSpec) => totalFrames(spec);
